import type { CarBrand, CarModel, CarVersion } from '@/types/quote.types';
import { executeForCRUD, type IRequestParams, GetCombo } from '../frontBack';
import { equalFilter, SORT_BY_VALUE, type CatalogFilter } from './catalogFilters';
import { mapPostalCodeRow, mapVersionRow, type PostalCodeResult } from './catalogMappers';

export interface VehicleType {
  id: string;
  name: string;
}

type ComboResponse = Awaited<ReturnType<typeof GetCombo>>;

const comboRows = (response: ComboResponse) =>
  response.success && Array.isArray(response.data) ? response.data : null;

const fetchCombo = (catalog: string, filters: CatalogFilter[]) =>
  GetCombo(catalog, JSON.stringify(filters), SORT_BY_VALUE);

const yearAndBrandFilters = (year: number, brandId: string): CatalogFilter[] => [
  equalFilter('Modelo', String(year), { conjunction: true }),
  equalFilter('Id_Marca', String(brandId), { conjunction: true }),
];

export const getBrands = async (year: number): Promise<CarBrand[]> => {
  const filters = [equalFilter('Modelo', String(year), { lowercaseItemId: true })];
  const rows = comboRows(await fetchCombo('Catalogo_Homologado_Modelos_Marcas', filters));
  return rows ? rows.map((item) => ({ id: item.Key, name: item.Value })) : [];
};

export const getModels = async (
  year: number,
  brandId: string,
  vehicleTypeId?: string | null
): Promise<CarModel[]> => {
  const filters = yearAndBrandFilters(year, brandId);
  if (vehicleTypeId) {
    filters.push(equalFilter('Tipo', String(vehicleTypeId), { conjunction: true }));
  }
  const rows = comboRows(await fetchCombo('Catalogo_Homologado_Qualitas', filters));
  return rows
    ? rows.map((item) => ({ id: String(item.Key), name: String(item.Value), brandId }))
    : [];
};

export const getVehicleTypes = async (
  year: number,
  brandId: string
): Promise<VehicleType[]> => {
  const filters = [
    equalFilter('Modelo', String(year), { conjunction: true }),
    equalFilter('Id_Marca', String(brandId)),
  ];
  const rows = comboRows(
    await fetchCombo('Catalogo_Homologado_Modelos_Marcas_Tipos', filters)
  );
  return rows ? rows.map((item) => ({ id: String(item.Key), name: String(item.Value) })) : [];
};

export const getPostalCodes = async (postalCode: string): Promise<PostalCodeResult[]> => {
  if (!postalCode || postalCode.length < 2) return [];

  const params: IRequestParams = {
    ClassName: 'Direcciones_CodigoPostal',
    Action: 'Get',
    CodigoPostal: postalCode,
  };
  const response = await executeForCRUD(params);
  if (response.success && Array.isArray(response.data)) {
    return response.data.map((item) => mapPostalCodeRow(item, postalCode));
  }
  return [];
};

export const getVersions = async (
  year: number,
  brandId: string,
  modelId: string
): Promise<CarVersion[]> => {
  const params: IRequestParams = {
    ClassName: 'VC_Versiones',
    Action: 'GetGrid',
    Year: year,
    Id_Marca: brandId,
    Id_Modelo: modelId,
    GetDataModel: 'false',
    GetDataTable: 'false',
  };
  const response = await executeForCRUD(params);
  if (response.success && Array.isArray(response.data)) {
    return response.data.map((item: unknown) => mapVersionRow(item, modelId));
  }
  return [];
};
